import { Checkbox } from "@design";
import { File, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatBytes, formatDate } from "@/i18n/format";
import type { FolderFile } from "./folderFiles";

const TEXT_EXTENSIONS = new Set(["pdf", "doc", "docx", "txt", "md"]);

/** A pasted folder's files, each with a checkbox; files chat can't take, or past the cap, can't be ticked. */
export function FolderFileList({
  files,
  selected,
  full,
  onToggle,
}: Readonly<{
  files: FolderFile[];
  selected: string[];
  /** The message can't take more files: unticked ones are disabled. */
  full: boolean;
  onToggle: (id: string, on: boolean) => void;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "folder" });
  const detail = ({ item }: FolderFile) =>
    [item.size == null ? null : formatBytes(item.size), item.last_modified ? formatDate(item.last_modified) : null]
      .filter(Boolean)
      .join(" · ");

  return (
    <ul className="flex max-h-80 flex-col divide-y divide-border-subtle overflow-y-auto">
      {files.map((file) => {
        const checked = selected.includes(file.item.id);
        const ext = file.item.name.split(".").pop()?.toLowerCase() ?? "";
        return (
          <li key={file.item.id} className="flex items-start gap-3 py-2">
            <span className="mt-0.5 shrink-0 text-content-tertiary" aria-hidden>
              {TEXT_EXTENSIONS.has(ext) ? <FileText size={16} /> : <File size={16} />}
            </span>
            <div className="min-w-0 flex-1 break-words">
              <Checkbox
                label={file.item.name}
                description={file.usable ? detail(file) : t("unsupported")}
                checked={checked}
                disabled={!file.usable || (full && !checked)}
                onChange={(on) => onToggle(file.item.id, on)}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
